import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Switch, Route, Redirect, NavLink, useRouteMatch } from 'react-router-dom';

import axios from 'axios';

import Loading from '../../../components/common/loading';
import RowList from '../../../components/common/rowList';
import TextInput from '../../../components/common/inputs/textInput.jsx';
import ToggleInput from '../../../components/common/inputs/toggleInput.jsx';
import SelectInput from '../../../components/common/inputs/selectInput.jsx';
import NotificationManager from '../../../utils/notificationManager';
import configurationSections from '../configurationSections';
import DeviceConfigurationUsers from './deviceConfigurationUsers';
import DeviceConfigurationVlans from './deviceConfigurationVlans';
import DeviceConfigurationPduRoutes from './deviceConfigurationPduRoutes';
import DeviceConfigurationWanHealth from './deviceConfigurationWanHealth';
import DeviceConfigurationEthernetLink from './deviceConfigurationEthernetLink';
import DeviceConfigurationNetwork from './deviceConfigurationNetwork';
import DeviceConfigurationFirmware from './deviceConfigurationFirmware';
import DeviceConfigurationUnpair from './deviceConfigurationUnpair';
import './devicePageConfiguration.css';

const styles = {
  container: {
    padding: '24px',
    overflow: "auto"
  },
  header: {
    marginBottom: '24px',
  },
  headerTitle: {
    margin: 0,
    fontSize: '22px',
    fontWeight: 600,
    color: 'var(--app-text)',
  },
  headerSubtitle: {
    marginTop: '8px',
    color: 'var(--app-text-muted)',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 260px) 1fr',
    gap: '24px',
    alignItems: 'flex-start',
  },
  sidebar: {
    background: 'var(--app-surface-1)',
    borderRadius: 8,
    border: '1px solid var(--app-border)',
    boxShadow: 'none',
    padding: '8px 0',
  },
  sidebarList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  sidebarLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 18px',
    color: 'var(--app-text-muted)',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'background 0.2s ease, color 0.2s ease',
  },
  sidebarLinkActive: {
    color: 'var(--app-text)',
    background: 'var(--app-surface-3)',
    borderLeft: '3px solid var(--app-border-strong)',
  },
  sidebarLinkIcon: {
    fontSize: '18px',
    color: 'inherit',
  },
  categoryGroup: {
    padding: '8px 0 12px 0',
    borderBottom: '1px solid var(--app-border)',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--app-text-soft)',
    padding: '6px 18px',
  },
  categoryIcon: {
    fontSize: '16px',
  },
  content: {
    minHeight: '320px',
  },
  detailWrap: {
    background: 'var(--app-surface-1)',
    borderRadius: 8,
    border: '1px solid var(--app-border)',
    boxShadow: 'none',
    padding: '24px',
  },
  detailLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '220px',
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--app-text-muted)',
    background: 'var(--app-surface-1)',
    borderRadius: 8,
    border: '1px solid var(--app-border)',
    boxShadow: 'none',
  },
  detailHeader: {
    marginBottom: '8px',
  },
  detailTitle: {
    margin: 0,
    fontSize: '20px',
    color: 'var(--app-text)',
    fontWeight: 600,
  },
  detailDescription: {
    marginTop: '8px',
    color: 'var(--app-text-muted)',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  permissionBanner: {
    marginBottom: '16px',
    padding: '12px 16px',
    background: 'var(--app-surface-2)',
    borderRadius: 6,
    color: 'var(--app-text-muted)',
    fontSize: '13px',
  },
  formSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid var(--app-border)',
  },
  firstFormSection: {
    borderTop: 'none',
    paddingTop: 0,
  },
  formSectionHeader: {
    marginBottom: '16px',
  },
  formSectionTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--app-text)',
  },
  formSectionDescription: {
    marginTop: '6px',
    fontSize: '13px',
    color: 'var(--app-text-muted)',
    lineHeight: 1.6,
  },
  formFields: {
    display: 'grid',
    rowGap: '18px',
  },
  fieldWrapper: {
    background: 'var(--app-surface-2)',
    borderRadius: 8,
    padding: '18px 20px',
  },
  actions: {
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  errorText: {
    color: 'var(--danger-color)',
    fontSize: '13px',
  },
  detailSummary: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid var(--app-border)',
  },
  detailSummaryTitle: {
    margin: '0 0 12px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--app-text)',
  },
  sectionIntro: {
    marginBottom: '16px',
  },
  sectionIntroCategory: {
    textTransform: 'uppercase',
    fontSize: '12px',
    letterSpacing: '0.5px',
    color: 'var(--app-text-soft)',
    margin: 0,
  },
  sectionIntroTitle: {
    margin: '4px 0',
    fontSize: '22px',
    fontWeight: 600,
    color: 'var(--app-text)',
  },
  sectionIntroDescription: {
    margin: 0,
    color: 'var(--app-text-muted)',
    fontSize: '14px',
  },
  sectionContent: {
    marginTop: '16px',
  },
};

const CATEGORY_META = {
  system: { label: 'System', icon: 'settings' },
  network: { label: 'Network', icon: 'lan' },
  advancedNetwork: { label: 'Advanced Network', icon: 'lan' },
  settings: { label: 'Settings', icon: 'tune' },
  maintenance: { label: 'Maintenance', icon: 'build' },
};

const CATEGORY_ORDER = ['system', 'network', 'advancedNetwork', 'settings', 'maintenance'];

const removeTrailingSlash = (value = '') => (value.endsWith('/') ? value.slice(0, -1) : value);

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  if (typeof value === 'number') return value !== 0;
  return Boolean(value);
};

const DeviceConfigurationSectionForm = ({
  section,
  device,
  conf,
  loading,
  refreshConf,
  onDeviceDataUpdate,
  user,
  deviceOffline,
  offlineReason,
  hideHeader = false,
}) => {
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const context = useMemo(() => ({ conf, device }), [conf, device]);

  const fields = useMemo(() => {
    return (section.formSections || []).flatMap((group) => group.fields || []);
  }, [section]);

  const detailRows = useMemo(() => {
    if (!section.showDetailFields || !Array.isArray(section.detailFields)) {
      return [];
    }

    return section.detailFields
      .map((field) => {
        const value =
          typeof field.value === 'function' ? field.value(context) : field.value;
        const disabled =
          typeof field.disabled === 'function'
            ? field.disabled(context)
            : field.disabled;

        return {
          ...field,
          value,
          disabled,
        };
      })
      .filter(Boolean);
  }, [context, section]);

  const initialValues = useMemo(() => {
    const defaults = {};
    fields.forEach((field) => {
      let value;
      if (field.getInitialValue) {
        value = field.getInitialValue(context);
      } else if (field.type === 'toggle') {
        value = normalizeBoolean(conf?.[field.name]);
      } else if (field.type === 'select') {
        const raw = conf?.[field.name];
        value = raw === undefined || raw === null ? '' : String(raw);
      } else {
        value = conf?.[field.name] ?? '';
      }

      if (field.type === 'toggle') {
        defaults[field.name] = normalizeBoolean(value);
      } else if (field.type === 'select') {
        defaults[field.name] = value ?? '';
      } else if (field.type === 'number' && value !== '' && value !== null && value !== undefined) {
        defaults[field.name] = Number(value);
      } else {
        defaults[field.name] = value ?? '';
      }
    });
    return defaults;
  }, [fields, context, conf]);

  useEffect(() => {
    setFormValues(initialValues);
    setError('');
  }, [initialValues]);

  const hasEditPermission = user?.role?.editDeviceInfo !== false;
  const offline = !!deviceOffline;
  const canEdit = hasEditPermission && !offline;

  const handleChange = useCallback(
    (fieldName, value) => {
      setFormValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
      setError('');
    },
    [setError]
  );

  const isDirty = useMemo(() => {
    return fields.some((field) => {
      const name = field.name;
      const current = formValues[name];
      const initial = initialValues[name];
      if (field.compare) {
        return !field.compare(current, initial, {
          formValues,
          initialValues,
          conf,
          device,
        });
      }
      return current !== initial;
    });
  }, [fields, formValues, initialValues, conf, device]);

  const deviceSerial = conf?.enclosureSerialNumber || device?.enclosureSerialNumber;

  const handleReset = useCallback(() => {
    setFormValues(initialValues);
    setError('');
  }, [initialValues]);

  const handleSubmit = useCallback(async () => {
    if (!canEdit || saving || !deviceSerial) {
      return;
    }

    const payload = {};
    fields.forEach((field) => {
      const name = field.name;
      const current = formValues[name];
      const initial = initialValues[name];

      const hasChanged = field.compare
        ? !field.compare(current, initial, {
            formValues,
            initialValues,
            conf,
            device,
          })
        : current !== initial;

      if (!hasChanged) {
        return;
      }

      const valueToTransform = current;
      const transformed = field.transformOut
        ? field.transformOut(valueToTransform, { formValues, conf, device })
        : valueToTransform;

      if (transformed === undefined) {
        return;
      }

      payload[field.payloadKey || name] = transformed;
    });

    if (!Object.keys(payload).length) {
      setError('No changes detected for this section.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      await axios.put(`/v1/conf/${deviceSerial}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (onDeviceDataUpdate) {
        const nextDevicePatch = {};
        if (payload.globalRebootTime !== undefined) {
          nextDevicePatch.globalRebootTime = payload.globalRebootTime;
        }
        if (Object.keys(nextDevicePatch).length) {
          onDeviceDataUpdate(nextDevicePatch);
        }
      }
      NotificationManager.success('Configuration updated successfully.');
      if (refreshConf) {
        refreshConf({ useCache: false, force: true });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to update configuration. Please try again.';
      setError(message);
      NotificationManager.alert(message);
    } finally {
      setSaving(false);
    }
  }, [
    canEdit,
    conf,
    device,
    deviceSerial,
    fields,
    formValues,
    initialValues,
    refreshConf,
    onDeviceDataUpdate,
    saving,
  ]);

  if (loading) {
    return (
      <div style={{ ...styles.detailWrap, ...styles.detailLoading }}>
        <Loading size="32px" black />
      </div>
    );
  }

  if (!conf) {
    return (
      <div style={styles.emptyState}>
        Configuration data will appear once the latest device configuration finishes loading.
      </div>
    );
  }

  return (
    <div style={styles.detailWrap}>
      {!hideHeader && (
        <div style={styles.detailHeader}>
          <h4 style={styles.detailTitle}>{section.label}</h4>
          <p style={styles.detailDescription}>{section.description}</p>
        </div>
      )}
      {!canEdit && (
        <div style={styles.permissionBanner}>
          {offline
            ? offlineReason ||
              'Device is offline. Configuration changes are disabled until it reconnects.'
            : 'You can view this configuration, but your role does not allow editing device settings.'}
        </div>
      )}
      {detailRows.length ? (
        <section style={styles.detailSummary}>
          <h5 style={styles.detailSummaryTitle}>Current Device Information</h5>
          <RowList rows={detailRows} />
        </section>
      ) : null}
      {(section.formSections || []).map((group, index) => (
        <section
          key={group.title || `${section.key}-group-${index}`}
          style={{
            ...styles.formSection,
            ...(index === 0 ? styles.firstFormSection : {}),
          }}
        >
          <header style={styles.formSectionHeader}>
            <h5 style={styles.formSectionTitle}>{group.title}</h5>
            {group.description ? (
              <p style={styles.formSectionDescription}>{group.description}</p>
            ) : null}
          </header>
          <div style={styles.formFields}>
            {(group.fields || []).map((field) => {
              const fieldValue = formValues[field.name];
              const computedDisabled =
                !canEdit ||
                saving ||
                (typeof field.disabled === 'function'
                  ? field.disabled(formValues)
                  : field.disabled);

              let input = null;
              if (field.type === 'toggle') {
                input = (
                  <ToggleInput
                    label={field.label}
                    value={normalizeBoolean(fieldValue)}
                    callback={(value) => handleChange(field.name, value)}
                    disabled={computedDisabled}
                    toggleLabelOff={field.toggleLabelOff || 'Off'}
                    toggleLabelOn={field.toggleLabelOn || 'On'}
                    description={field.description}
                    id={`${section.key}-${field.name}`}
                  />
                );
              } else if (field.type === 'select') {
                input = (
                  <SelectInput
                    label={field.label}
                    value={fieldValue ?? ''}
                    callback={(value) => handleChange(field.name, value)}
                    disabled={computedDisabled}
                    options={field.options || []}
                    defaultSelectText={field.defaultSelectText || 'Select option'}
                    description={field.description}
                  />
                );
              } else {
                const inputType = field.type === 'password' ? 'password' : field.type || 'text';
                input = (
                  <TextInput
                    label={field.label}
                    value={fieldValue ?? ''}
                    callback={(value) => handleChange(field.name, value)}
                    disabled={computedDisabled}
                    placeholder={field.placeholder}
                    type={inputType}
                    min={field.min}
                    max={field.max}
                    description={field.description}
                    inputContainerStyle={{ marginBottom: 0 }}
                  />
                );
              }

              return (
                <div key={field.name} style={styles.fieldWrapper}>
                  {input}
                </div>
              );
            })}
          </div>
        </section>
      ))}
      <div style={styles.actions}>
        <button
          type="button"
          className="button"
          onClick={handleSubmit}
          disabled={!canEdit || !isDirty || saving || !deviceSerial}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        <button
          type="button"
          className="button secondary"
          onClick={handleReset}
          disabled={!canEdit || saving || !isDirty}
        >
          Reset
        </button>
        {error ? <span style={styles.errorText}>{error}</span> : null}
      </div>
    </div>
  );
};

export default function DevicePageConfiguration({
  device,
  conf,
  confLoading,
  refreshConf,
  user,
  deviceOffline,
  offlineReason,
  resources,
  fetchFwStatus,
  deviceLoaded,
  effectivePermissions,
  unpair,
  onDeviceDataUpdate,
}) {
  const configurationMatch = useRouteMatch(
    '/devices/connected/:enclosureSerialNumber/configuration/:sectionKey?'
  );
  const currentMatch = useRouteMatch();
  const routeMatch = configurationMatch || currentMatch;
  const mobileNavRef = useRef(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const routeSerial =
    configurationMatch?.params?.enclosureSerialNumber ||
    device?.enclosureSerialNumber ||
    conf?.enclosureSerialNumber;
  const baseUrl = routeSerial
    ? `/devices/connected/${routeSerial}/configuration`
    : removeTrailingSlash(routeMatch?.url || '');
  const basePath = '/devices/connected/:enclosureSerialNumber/configuration';
  const isAdmin = Boolean(user?.is_enterprise_admin || user?.has_admin_privileges);

  const navSections = configurationSections
    .filter((section) => {
      if (section.key === 'firmware' || section.key === 'unpair') {
        return isAdmin;
      }
      return true;
    })
    .map((section) => ({
      ...section,
      path: `${baseUrl}/${section.key}`,
    }));

  const defaultPath = navSections[0]?.path || baseUrl;
  const customComponents = useMemo(
    () => ({
      DeviceUsers: DeviceConfigurationUsers,
      DeviceVlans: DeviceConfigurationVlans,
      DevicePduRoutes: DeviceConfigurationPduRoutes,
      DeviceWanHealth: DeviceConfigurationWanHealth,
      DeviceEthernetLink: DeviceConfigurationEthernetLink,
      DeviceNetwork: DeviceConfigurationNetwork,
      DeviceFirmware: DeviceConfigurationFirmware,
      DeviceUnpair: DeviceConfigurationUnpair,
    }),
    []
  );
  const navCategories = useMemo(() => {
    return CATEGORY_ORDER.map((categoryKey) => {
      const sections = navSections.filter((section) => section.category === categoryKey);
      if (!sections.length) {
        return null;
      }
      return {
        key: categoryKey,
        label: CATEGORY_META[categoryKey]?.label || categoryKey,
        icon: CATEGORY_META[categoryKey]?.icon || 'folder',
        sections,
      };
    }).filter(Boolean);
  }, [navSections]);

  const activeSectionKey = configurationMatch?.params?.sectionKey || navSections[0]?.key;
  const activeSection =
    navSections.find((section) => section.key === activeSectionKey) || navSections[0];
  const activeCategory = navCategories.find((category) =>
    category.sections.some((section) => section.key === activeSection?.key)
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeSectionKey]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (mobileNavRef.current && !mobileNavRef.current.contains(event.target)) {
        setMobileNavOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileNavOpen]);

  const renderSectionIntro = useCallback(
    (section) => {
      if (!section) return null;
      const categoryInfo = section.category ? CATEGORY_META[section.category] : null;
      return (
        <div style={styles.sectionIntro}>
          {categoryInfo ? (
            <p style={styles.sectionIntroCategory}>{categoryInfo.label}</p>
          ) : null}
          <h3 style={styles.sectionIntroTitle}>{section.label}</h3>
          {section.description ? (
            <p style={styles.sectionIntroDescription}>{section.description}</p>
          ) : null}
        </div>
      );
    },
    []
  );

  const showSectionIntro =
    activeSection &&
    (!activeSection.customComponent ||
      activeSection.customComponent === 'DeviceNetwork');

  return (
    <div className="device-config-shell">
      {/* {deviceOffline ? (
        <div className="callout warning" style={{ marginBottom: '16px' }}>
          {offlineReason || 'Device is offline. Configuration changes are temporarily unavailable.'}
        </div>
      ) : null} */}
      <div
        className="device-config-mobile-nav"
        ref={mobileNavRef}
        aria-label="Configuration section selector"
      >
        <button
          type="button"
          className="device-config-mobile-nav__trigger"
          aria-controls="device-config-mobile-menu"
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <span className="device-config-mobile-nav__trigger-copy">
            <span className="device-config-mobile-nav__eyebrow">Configuration</span>
            <span className="device-config-mobile-nav__active-section">
              {activeSection?.icon ? (
                <i className="material-icons" aria-hidden="true">
                  {activeSection.icon}
                </i>
              ) : null}
              <span>
                {activeCategory?.label ? `${activeCategory.label} / ` : ''}
                {activeSection?.label || 'Select section'}
              </span>
            </span>
          </span>
          <i className="material-icons device-config-mobile-nav__chevron" aria-hidden="true">
            {mobileNavOpen ? 'expand_less' : 'expand_more'}
          </i>
        </button>
        {mobileNavOpen ? (
          <nav
            id="device-config-mobile-menu"
            className="app-card device-config-mobile-nav__menu"
            aria-label="Mobile configuration sections"
          >
            {navCategories.map((category) => (
              <section
                key={category.key}
                className="device-config-mobile-nav__group"
                aria-labelledby={`device-config-mobile-${category.key}`}
              >
                <div
                  id={`device-config-mobile-${category.key}`}
                  className="device-config-mobile-nav__group-title"
                >
                  <i className="material-icons" aria-hidden="true">
                    {category.icon}
                  </i>
                  <span>{category.label}</span>
                </div>
                <div className="device-config-mobile-nav__items">
                  {category.sections.map((section) => (
                    <NavLink
                      key={section.path}
                      to={section.path}
                      className="device-config-mobile-nav__item"
                      activeClassName="device-config-mobile-nav__item--active"
                      isActive={() => section.key === activeSectionKey}
                      onClick={() => setMobileNavOpen(false)}
                    >
                      <i className="material-icons" aria-hidden="true">
                        {section.icon}
                      </i>
                      <span>{section.label}</span>
                      {section.key === activeSectionKey ? (
                        <i
                          className="material-icons device-config-mobile-nav__item-check"
                          aria-hidden="true"
                        >
                          check
                        </i>
                      ) : null}
                    </NavLink>
                  ))}
                </div>
              </section>
            ))}
          </nav>
        ) : null}
      </div>
      <div className="device-config-layout">
        <nav className="app-card device-config-sidebar" aria-label="Configuration sections">
          {navCategories.map((category) => (
            <div key={category.key} style={styles.categoryGroup}>
              <div style={styles.categoryHeader}>
                <i className="material-icons" style={styles.categoryIcon}>
                  {category.icon}
                </i>
                <span>{category.label}</span>
              </div>
              <ul style={styles.sidebarList}>
                {category.sections.map((section) => (
                  <li key={section.path}>
                    <NavLink
                      to={section.path}
                      style={styles.sidebarLink}
                      activeStyle={styles.sidebarLinkActive}
                      isActive={() => section.key === activeSectionKey}
                    >
                      <i className="material-icons" style={styles.sidebarLinkIcon}>
                        {section.icon}
                      </i>
                      <span>{section.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="device-config-content">
          {showSectionIntro ? renderSectionIntro(activeSection) : null}
          <Switch>
            <Route exact path={basePath}>
              <Redirect to={defaultPath} />
            </Route>
            {navSections.map((section) => {
              const CustomComponent = section.customComponent
                ? customComponents[section.customComponent]
                : null;

              return (
                <Route key={section.key} path={`${basePath}/${section.key}`} exact>
                  <div style={styles.sectionContent}>
                    {CustomComponent ? (
                      <CustomComponent
                        section={section}
                        device={device}
                        conf={conf}
                        confLoading={confLoading}
                        refreshConf={refreshConf}
                        user={user}
                        deviceOffline={deviceOffline}
                        offlineReason={offlineReason}
                        resources={resources}
                        fetchFwStatus={fetchFwStatus}
                        deviceLoaded={deviceLoaded}
                        effectivePermissions={effectivePermissions}
                        unpair={unpair}
                        onDeviceDataUpdate={onDeviceDataUpdate}
                        hideHeader={
                          Boolean(showSectionIntro) &&
                          section.customComponent === 'DeviceNetwork'
                        }
                      />
                    ) : (
                      <DeviceConfigurationSectionForm
                        section={section}
                        device={device}
                        conf={conf}
                        loading={confLoading}
                        refreshConf={refreshConf}
                        onDeviceDataUpdate={onDeviceDataUpdate}
                        user={user}
                        deviceOffline={deviceOffline}
                        offlineReason={offlineReason}
                        hideHeader={Boolean(showSectionIntro)}
                      />
                    )}
                  </div>
                </Route>
              );
            })}
            <Route path={`${basePath}/:sectionKey`}>
              <Redirect to={defaultPath} />
            </Route>
        </Switch>
      </div>
      </div>
    </div>
  );
}
